import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Code, Plus, Play, Star, Copy, Download, Upload, Edit2, Trash2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { getVersionInfo } from '../debugVersion';

const PromptStudio = () => {
  const toast = useToast();
  const debugVersion = getVersionInfo();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [variables, setVariables] = useState({});
  const [generatedText, setGeneratedText] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationStatus, setGenerationStatus] = useState('');
  const [isStreaming, setIsStreaming] = useState(true);
  const [streamingMetadata, setStreamingMetadata] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);
  const [showDebugPanel, setShowDebugPanel] = useState(true);
  const [generationStartTime, setGenerationStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [lastEventTime, setLastEventTime] = useState(null);
  const [blockageDetected, setBlockageDetected] = useState(false);
  const [generationId, setGenerationId] = useState(null);
  const [stepTimings, setStepTimings] = useState({});
  const [generationSummary, setGenerationSummary] = useState(null);
  const [aiProvider, setAiProvider] = useState(null);
  const [aiModel, setAiModel] = useState(null);
  const [lastHttpStatus, setLastHttpStatus] = useState(null);
  const [lastErrorMessage, setLastErrorMessage] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'Marketing',
    template: '',
    variables: []
  });

  const categories = ['Marketing', 'E-commerce', 'Social Media', 'Blog', 'Email', 'Ads', 'Custom'];

  useEffect(() => {
    fetchPrompts();
  }, []);

  // Chronomètre : mise à jour chaque seconde pendant la génération
  useEffect(() => {
    if (generating && generationStartTime) {
      const interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - generationStartTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [generating, generationStartTime]);

  // Détection de blocage : si aucun événement pendant 10 secondes
  useEffect(() => {
    if (generating && lastEventTime && !blockageDetected) {
      const interval = setInterval(() => {
        const timeSinceLastEvent = Math.floor((Date.now() - lastEventTime) / 1000);
        if (timeSinceLastEvent >= 10) {
          setBlockageDetected(true);
          const currentStepInfo = getCurrentPipelineStep();
          addDebugLog(`⚠️ BLOCAGE DÉTECTÉ : Aucun événement depuis 10 secondes (bloqué à l'étape ${currentStepInfo.step}/${currentStepInfo.total})`, 'warning');
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [generating, lastEventTime, blockageDetected]);

  // Fonction helper pour calculer l'étape actuelle du pipeline
  const getCurrentPipelineStep = () => {
    const TOTAL_STEPS = 7;
    let step = 0;
    
    // Analyse des logs pour déterminer l'étape
    const hasPostRequest = debugLogs.some(log => log.message.includes('Sending POST request'));
    const hasHttpResponse = debugLogs.some(log => log.message.includes('Response received'));
    const hasStreamStart = debugLogs.some(log => log.message.includes('Starting to read SSE stream'));
    const hasChunk = debugLogs.some(log => log.message.includes('Chunk #'));
    const hasStartEvent = debugLogs.some(log => log.message.includes('Stream START event'));
    const hasDelta = debugLogs.some(log => log.message.includes('Delta #'));
    const hasFinished = debugLogs.some(log => log.message.includes('STREAM FINISHED'));
    
    if (hasFinished) step = 7;
    else if (hasDelta) step = 6;
    else if (hasStartEvent) step = 5;
    else if (hasChunk) step = 4;
    else if (hasStreamStart) step = 3;
    else if (hasHttpResponse) step = 2;
    else if (hasPostRequest) step = 1;
    
    return { step, total: TOTAL_STEPS };
  };

  // Fonction helper pour formater le temps écoulé (MM:SS)
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Fonction helper pour générer un identifiant court
  const generateShortId = () => {
    return crypto.randomUUID().slice(0, 13);
  };

  // Fonction helper pour formater l'horodatage complet
  const getFullTimestamp = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const ms = String(now.getMilliseconds()).padStart(3, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}.${ms}`;
  };

  // Fonction helper pour enregistrer le timing d'une étape
  const recordStepTiming = (stepName) => {
    const now = Date.now();
    setStepTimings(prev => ({
      ...prev,
      [stepName]: {
        timestamp: now,
        elapsed: generationStartTime ? now - generationStartTime : 0
      }
    }));
  };

  // Fonction helper pour déterminer l'origine de l'erreur (Signature)
  const determineErrorOrigin = (httpStatus, errorMessage, headers) => {
    // Frontend (erreurs réseau)
    if (!httpStatus || httpStatus === 0) {
      return { origin: 'Frontend', confidence: 'High', reason: 'Network error or CORS issue' };
    }
    
    // Backend Middleware (Rate Limiting)
    if (httpStatus === 429) {
      if (errorMessage && errorMessage.includes('Too many AI requests')) {
        return { origin: 'Backend Middleware', confidence: 'High', reason: 'AI Rate Limiter triggered' };
      }
      if (errorMessage && errorMessage.includes('Too many requests')) {
        return { origin: 'Backend Middleware', confidence: 'High', reason: 'Global Rate Limiter triggered' };
      }
      return { origin: 'Backend Middleware', confidence: 'Medium', reason: 'HTTP 429 detected' };
    }
    
    // Backend API (erreurs 4xx hors 429)
    if (httpStatus >= 400 && httpStatus < 500) {
      return { origin: 'Backend API', confidence: 'High', reason: `HTTP ${httpStatus} client error` };
    }
    
    // Provider IA (erreurs 5xx ou timeout)
    if (httpStatus >= 500) {
      if (errorMessage && (errorMessage.includes('timeout') || errorMessage.includes('quota') || errorMessage.includes('provider'))) {
        return { origin: 'Provider IA (Emergent/OpenAI)', confidence: 'High', reason: 'Provider error or timeout' };
      }
      return { origin: 'Backend API', confidence: 'Medium', reason: `HTTP ${httpStatus} server error` };
    }
    
    // Réseau
    if (errorMessage && (errorMessage.includes('network') || errorMessage.includes('fetch') || errorMessage.includes('CORS'))) {
      return { origin: 'Réseau', confidence: 'High', reason: 'Network connectivity issue' };
    }
    
    // Inconnue
    return { origin: 'Inconnue', confidence: 'Low', reason: 'Unable to determine from available data' };
  };

  // Fonction helper pour générer la RCA préliminaire automatique
  const generatePreliminaryRCA = (logs, httpStatus, errorMessage, provider, model) => {
    // Analyse des événements reçus
    const hasPostRequest = logs.some(log => log.message.includes('Sending POST request'));
    const hasHttpResponse = logs.some(log => log.message.includes('Response received'));
    const hasStreamStart = logs.some(log => log.message.includes('Starting to read SSE stream'));
    const hasChunk = logs.some(log => log.message.includes('Chunk #'));
    const hasStartEvent = logs.some(log => log.message.includes('Stream START event'));
    const hasDelta = logs.some(log => log.message.includes('Delta #'));
    const hasFinished = logs.some(log => log.message.includes('STREAM FINISHED'));
    const hasError = logs.some(log => log.type === 'error');
    const hasBlockage = logs.some(log => log.message.includes('BLOCAGE DÉTECTÉ'));
    
    // Dernier événement reçu
    let lastEventReceived = 'None';
    if (hasFinished) lastEventReceived = 'STREAM FINISHED';
    else if (hasDelta) lastEventReceived = `Delta (${logs.filter(l => l.message.includes('Delta #')).length} tokens)`;
    else if (hasStartEvent) lastEventReceived = 'Stream START event';
    else if (hasChunk) lastEventReceived = 'First SSE chunk';
    else if (hasStreamStart) lastEventReceived = 'Stream opening initiated';
    else if (hasHttpResponse) lastEventReceived = `HTTP ${httpStatus || 'response'}`;
    else if (hasPostRequest) lastEventReceived = 'POST request sent';
    
    // Dernier événement attendu (ce qui devrait suivre)
    let lastEventExpected = 'Unknown';
    if (hasStartEvent && !hasDelta) lastEventExpected = 'First Delta (token from AI)';
    else if (hasStreamStart && !hasStartEvent) lastEventExpected = 'Stream START event';
    else if (hasHttpResponse && !hasStreamStart && httpStatus === 200) lastEventExpected = 'SSE stream opening';
    else if (hasPostRequest && !hasHttpResponse) lastEventExpected = 'HTTP response';
    else if (hasDelta && !hasFinished) lastEventExpected = 'STREAM FINISHED event';
    
    // Étape du pipeline
    const stepInfo = getCurrentPipelineStep();
    const pipelineStage = `${stepInfo.step}/${stepInfo.total}`;
    
    // Déterminer origine et confiance
    const errorOrigin = httpStatus && hasError 
      ? determineErrorOrigin(httpStatus, errorMessage, {})
      : { origin: 'Inconnue', confidence: 'Low', reason: 'No error detected or insufficient data' };
    
    // Preuves observées
    const evidences = [];
    if (hasPostRequest) evidences.push('POST request sent');
    if (hasHttpResponse) evidences.push(`HTTP ${httpStatus || '?'} received`);
    if (hasStreamStart) evidences.push('SSE stream opened');
    if (hasChunk) evidences.push(`${logs.filter(l => l.message.includes('Chunk #')).length} SSE chunks received`);
    if (hasStartEvent) evidences.push('Stream START event received');
    if (hasDelta) evidences.push(`${logs.filter(l => l.message.includes('Delta #')).length} deltas (tokens) received`);
    if (hasBlockage) evidences.push('10s timeout detected');
    if (hasError) evidences.push(`Error: ${errorMessage || 'Unknown'}`);
    if (hasFinished) evidences.push('STREAM FINISHED event received');
    
    return {
      origin: errorOrigin.origin,
      confidence: errorOrigin.confidence,
      reason: errorOrigin.reason,
      evidences: evidences.join('; '),
      pipelineStage,
      lastEventReceived,
      lastEventExpected,
      httpStatus: httpStatus || 'N/A',
      provider: provider || 'N/A',
      model: model || 'N/A'
    };
  };

  const fetchPrompts = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts`,
        { credentials: 'include' }
      );
      const data = await response.json();
      if (data.success) {
        setPrompts(data.prompts || []);
      }
    } catch (error) {
      console.error('Error fetching prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractVariables = (template) => {
    const regex = /\{\{(\w+)\}\}/g;
    const matches = [];
    let match;
    while ((match = regex.exec(template)) !== null) {
      if (!matches.includes(match[1])) {
        matches.push(match[1]);
      }
    }
    return matches;
  };

  const handleCreatePrompt = async (e) => {
    e.preventDefault();
    
    // If editing, use update endpoint
    if (editingPrompt) {
      return handleSaveEdit(e);
    }
    
    const vars = extractVariables(formData.template);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...formData, variables: vars })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Prompt créé avec succès');
        handleModalClose();
        fetchPrompts();
      }
    } catch (error) {
      console.error('Error creating prompt:', error);
      toast.error(`❌ Erreur lors de la création du prompt: ${error.message}`);
    }
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setFormData({
      name: prompt.name,
      description: prompt.description,
      category: prompt.category,
      template: prompt.template,
      variables: prompt.variables || []
    });
    setShowCreateModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    const vars = extractVariables(formData.template);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts/${editingPrompt.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ...formData, variables: vars })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Prompt modifié avec succès');
        handleModalClose();
        fetchPrompts();
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      toast.error(`❌ Erreur lors de la mise à jour du prompt: ${error.message}`);
    }
  };

  const handleDeletePrompt = async (promptId, promptName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le prompt "${promptName}" ?`)) {
      return;
    }
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts/${promptId}`,
        {
          method: 'DELETE',
          credentials: 'include'
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }
      
      const data = await response.json();
      if (data.success) {
        toast.success('✅ Prompt supprimé avec succès');
        fetchPrompts();
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      toast.error(`❌ Erreur lors de la suppression du prompt: ${error.message}`);
    }
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingPrompt(null);
    setFormData({ name: '', description: '', category: 'Marketing', template: '', variables: [] });
  };

  const handleExecutePrompt = async (prompt) => {
    console.log('[RCA-DEBUG] Execute prompt clicked');
    console.log('[RCA-DEBUG] Prompt ID:', prompt.id);
    console.log('[RCA-DEBUG] Prompt name:', prompt.name);
    
    setSelectedPrompt(prompt);
    const vars = {};
    prompt.variables.forEach(v => vars[v] = '');
    setVariables(vars);
    setGeneratedText('');
    setStreamingMetadata(null);
    setShowExecuteModal(true);
  };

  const handleGenerateStreaming = async () => {
    setGenerating(true);
    setGeneratedText('');
    setStreamingMetadata(null);
    setGenerationStatus('🔄 Préparation de la génération...');
    setDebugLogs([]);
    setShowDebugPanel(true);
    setGenerationStartTime(Date.now());
    setElapsedTime(0);
    setLastEventTime(Date.now());
    setBlockageDetected(false);
    const genId = generateShortId();
    setGenerationId(genId);
    setStepTimings({});
    setGenerationSummary(null);
    
    const addDebugLog = (message, type = 'info') => {
      const timestamp = getFullTimestamp();
      setDebugLogs(prev => [...prev, { timestamp, message, type }]);
      setLastEventTime(Date.now());
      console.log(`[DEBUG] ${message}`);
    };
    
    addDebugLog('═══════════════════════════════════════════════════════', 'header');
    addDebugLog('🔍 STREAMING GENERATION STARTED', 'header');
    addDebugLog('═══════════════════════════════════════════════════════', 'header');
    addDebugLog(`Generation ID: ${genId}`);
    addDebugLog(`Prompt ID: ${selectedPrompt.id}`);
    addDebugLog(`Prompt name: ${selectedPrompt.name}`);
    addDebugLog(`Variables count: ${Object.keys(variables).length}`);
    
    const controller = new AbortController();
    setAbortController(controller);

    // Watchdog: si aucun token n'arrive, on abandonne avec un message clair
    // plutot que de rester bloque indefiniment sur "Connexion au modele...".
    const STALL_TIMEOUT_MS = 75000;
    let stallTimer = null;
    let stalled = false;
    
    // Variables locales pour timings précis
    let timingStep1 = null;
    let timingStep2 = null;
    let timingStep3 = null;
    
    try {
      setGenerationStatus('🔌 Connexion au modèle IA...');
      timingStep1 = Date.now();
      const stepInfo = getCurrentPipelineStep();
      addDebugLog(`1️⃣ [Étape ${stepInfo.step + 1}/${stepInfo.total}] Sending POST request to /api/ai-studio/prompts/execute-stream`);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts/execute-stream`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            prompt_id: selectedPrompt.id,
            variables: variables
          }),
          signal: controller.signal
        }
      );

      timingStep2 = Date.now();
      const step1Duration = timingStep2 - timingStep1;
      addDebugLog(`2️⃣ [Étape ${getCurrentPipelineStep().step + 1}/${getCurrentPipelineStep().total}] (${step1Duration}ms) Response received: ${response.status} ${response.statusText}`);
      addDebugLog(`   Response OK: ${response.ok}`);

      if (!response.ok) {
        // === INSTRUMENTATION RCA : Capture complète erreur HTTP ===
        addDebugLog('═══════════════════════════════════════════════════════', 'header');
        addDebugLog('🔴 HTTP ERROR DETAILS', 'header');
        addDebugLog('═══════════════════════════════════════════════════════', 'header');
        addDebugLog(`❌ HTTP Status: ${response.status} ${response.statusText}`, 'error');
        addDebugLog(`   Endpoint: POST /api/ai-studio/prompts/execute-stream`, 'error');
        
        // Capture headers (dont Retry-After)
        const headers = Object.fromEntries(response.headers.entries());
        addDebugLog(`   Response Headers: ${JSON.stringify(headers, null, 2)}`, 'error');
        
        if (headers['retry-after']) {
          addDebugLog(`   ⏱️ Retry-After: ${headers['retry-after']}s`, 'warning');
        }
        
        // Capture body JSON complet
        const errorData = await response.json().catch(() => ({}));
        addDebugLog(`   Response Body: ${JSON.stringify(errorData, null, 2)}`, 'error');
        
        // Sauvegarder HTTP Status et Error Message pour RCA
        setLastHttpStatus(response.status);
        setLastErrorMessage(errorData.detail || errorData.error || `HTTP ${response.status}`);
        
        // Déterminer source de l'erreur
        const errorOriginResult = determineErrorOrigin(response.status, errorData.detail || errorData.error || '', headers);
        addDebugLog(`   🔍 Error Source: ${errorOriginResult.origin}`, 'error');
        addDebugLog(`   📊 Confidence: ${errorOriginResult.confidence}`, 'error');
        addDebugLog(`   📝 Reason: ${errorOriginResult.reason}`, 'error');
        addDebugLog('═══════════════════════════════════════════════════════', 'header');
        
        const errorMessage = errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(errorMessage);
      }

      addDebugLog(`3️⃣ [Étape ${getCurrentPipelineStep().step + 1}/${getCurrentPipelineStep().total}] Starting to read SSE stream...`);
      timingStep3 = Date.now();
      setGenerationStatus('✍️ Génération en cours...');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventCount = 0;

      const armStallWatchdog = () => {
        if (stallTimer) clearTimeout(stallTimer);
        stallTimer = setTimeout(() => {
          stalled = true;
          addDebugLog(`⏱️ STALL: aucun token recu depuis ${STALL_TIMEOUT_MS / 1000}s — abandon`, 'error');
          controller.abort();
        }, STALL_TIMEOUT_MS);
      };
      armStallWatchdog();

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          addDebugLog('✅ Stream reader finished (done=true)');
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        if (eventCount < 3) {
          addDebugLog(`📦 Chunk #${eventCount + 1} received (${chunk.length} bytes)`);
        }
        
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            eventCount++;
            try {
              const dataStr = line.slice(6);
              const data = JSON.parse(dataStr);
              
              addDebugLog(`Event #${eventCount}: ${data.type || 'unknown'}`);
              
              if (data.type === 'debug') {
                addDebugLog(`🔧 Backend: ${data.stage} - ${data.message}`, 'backend');
                if (data.details) {
                  addDebugLog(`   Details: ${JSON.stringify(data.details)}`);
                }
              } else if (data.type === 'start') {
                addDebugLog('🚀 Stream START event received');
                setStreamingMetadata(data.context);
                setGenerationStatus('✍️ Génération en cours...');
                
                // === INSTRUMENTATION RCA : Capture Provider et Model ===
                if (data.context && data.context.provider) {
                  setAiProvider(data.context.provider);
                  addDebugLog(`   Provider IA: ${data.context.provider}`);
                }
                if (data.context && data.context.model) {
                  setAiModel(data.context.model);
                  addDebugLog(`   Model IA: ${data.context.model}`);
                }
              } else if (data.type === 'delta') {
                if (eventCount <= 5) {
                  const preview = data.content.substring(0, 30).replace(/\n/g, ' ');
                  addDebugLog(`💬 Delta #${eventCount}: "${preview}..."`);
                }
                armStallWatchdog();
                setGeneratedText(prev => prev + data.content);
              } else if (data.type === 'done') {
                addDebugLog('✅ Stream DONE event received');
                setStreamingMetadata(prev => ({
                  ...prev,
                  rate_limit: data.rate_limit
                }));
                setGenerationStatus('✅ Réponse reçue');
                toast.success('✅ Génération terminée avec succès');
              } else if (data.type === 'error') {
                addDebugLog(`❌ Stream ERROR: ${data.message}`, 'error');
                setGenerationStatus('❌ Erreur lors de la génération');
                throw new Error(data.message || 'Erreur inconnue du serveur');
              }
            } catch (e) {
              // Only swallow genuine parse failures. A deliberate error thrown
              // for an SSE `error` event must reach the outer handler, or the
              // UI silently hangs instead of reporting the failure.
              if (e instanceof SyntaxError) {
                addDebugLog(`❌ Error parsing SSE event: ${e.message}`, 'error');
              } else {
                throw e;
              }
            }
          }
        }
      }
      
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
      addDebugLog(`✅ STREAM FINISHED - Total events: ${eventCount}`, 'success');
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
      
    } catch (error) {
      if (error.name === 'AbortError' && stalled) {
        addDebugLog('⏱️ STREAM BLOQUE — aucun token recu', 'error');
        addDebugLog(`   Le flux s'est ouvert mais aucun token n'est arrive en ${STALL_TIMEOUT_MS / 1000}s.`, 'error');
        addDebugLog('   Transmettez ce diagnostic au support Emergent.', 'error');
        setGenerationStatus('⏱️ Flux bloque — aucun token recu');
        toast.error('⏱️ Le flux s\'est ouvert mais aucun token n\'est arrive. Voir le panneau Debug.');
      } else if (error.name === 'AbortError') {
        addDebugLog('⏹️ Generation stopped by user', 'warning');
        setGenerationStatus('⏹️ Génération arrêtée');
        toast.info('⏹️ Génération arrêtée par l\'utilisateur');
      } else {
        addDebugLog('═══════════════════════════════════════════════════════', 'header');
        addDebugLog(`❌ ERROR: ${error.name}`, 'error');
        addDebugLog(`   Message: ${error.message}`, 'error');
        addDebugLog('═══════════════════════════════════════════════════════', 'header');
        setGenerationStatus('❌ Erreur');
        toast.error(`❌ Erreur de génération:\n${error.message}`);
      }
    } finally {
      if (stallTimer) clearTimeout(stallTimer);
      setGenerating(false);
      setAbortController(null);
      addDebugLog('🔚 Cleanup done');
      
      // Générer le résumé automatique
      const stepInfo = getCurrentPipelineStep();
      const chunksReceived = debugLogs.filter(log => log.message.includes('Chunk #')).length;
      const deltasReceived = debugLogs.filter(log => log.message.includes('Delta #')).length;
      const lastError = debugLogs.filter(log => log.type === 'error').slice(-1)[0];
      
      let status = '❌ Blocage';
      if (debugLogs.some(log => log.message.includes('STREAM FINISHED'))) {
        status = '✅ Succès';
      } else if (lastError) {
        status = '❌ Erreur';
      } else if (debugLogs.some(log => log.message.includes('stopped by user'))) {
        status = '⏹️ Arrêté';
      }
      
      const summary = {
        status,
        stepReached: `${stepInfo.step}/${stepInfo.total}`,
        totalTime: formatElapsedTime(elapsedTime),
        chunksReceived,
        tokensReceived: deltasReceived,
        error: lastError ? lastError.message : 'Aucune'
      };
      
      setGenerationSummary(summary);
      
      // Afficher le résumé dans les logs
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
      addDebugLog('📊 RÉSUMÉ DU DIAGNOSTIC', 'header');
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
      addDebugLog(`Statut: ${status}`);
      addDebugLog(`Étape atteinte: ${summary.stepReached}`);
      addDebugLog(`Temps total: ${summary.totalTime}`);
      addDebugLog(`Chunks reçus: ${chunksReceived}`);
      addDebugLog(`Tokens reçus: ${deltasReceived}`);
      addDebugLog(`Erreur: ${summary.error}`);
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
      
      // === INSTRUMENTATION RCA : Générer RCA préliminaire automatique ===
      const rca = generatePreliminaryRCA(
        debugLogs,
        lastHttpStatus,
        lastErrorMessage,
        aiProvider,
        aiModel
      );
      
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
      addDebugLog('🔬 RCA PRELIMINAIRE', 'header');
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
      addDebugLog(`Origine probable: ${rca.origin}`);
      addDebugLog(`Confiance: ${rca.confidence}`);
      addDebugLog(`Raison: ${rca.reason}`);
      addDebugLog(`Preuves observées: ${rca.evidences}`);
      addDebugLog(`Étape du pipeline: ${rca.pipelineStage}`);
      addDebugLog(`Dernier événement reçu: ${rca.lastEventReceived}`);
      addDebugLog(`Dernier événement attendu: ${rca.lastEventExpected}`);
      addDebugLog(`HTTP Status: ${rca.httpStatus}`);
      addDebugLog(`Provider IA: ${rca.provider}`);
      addDebugLog(`Model IA: ${rca.model}`);
      addDebugLog(`Generation ID: ${generationId || 'N/A'}`);
      addDebugLog('═══════════════════════════════════════════════════════', 'header');
    }
  };

  const handleGenerate = async () => {
    if (isStreaming) {
      return handleGenerateStreaming();
    }
    
    // Fallback: Non-streaming mode
    setGenerating(true);
    setGenerationStatus('🔄 Préparation de la génération...');
    
    try {
      setGenerationStatus('🔌 Connexion au modèle IA...');
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai-studio/prompts/execute`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            prompt_id: selectedPrompt.id,
            variables: variables
          })
        }
      );
      
      // Vérification HTTP status avant parsing
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.error || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }
      
      setGenerationStatus('✍️ Génération en cours...');
      const data = await response.json();
      
      if (data.success && data.generated_text) {
        setGeneratedText(data.generated_text);
        setGenerationStatus('✅ Réponse reçue');
        toast.success('✅ Génération terminée avec succès');
      } else {
        // Erreur backend retournée dans la réponse
        const errorMessage = data.error || 'La génération a échoué. Veuillez réessayer.';
        setGenerationStatus('❌ Erreur');
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error executing prompt:', error);
      setGenerationStatus('❌ Erreur');
      setGeneratedText('');
      toast.error(`❌ Erreur lors de la génération:\n${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleStopGeneration = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  const copyDebugLogs = () => {
    const stepInfo = getCurrentPipelineStep();
    const header = `🔍 NEXORA AI - Diagnostic Streaming (${new Date().toLocaleString('fr-FR')})\n`;
    const separator = '═'.repeat(60) + '\n';
    const versionInfo = `Debug Version: ${debugVersion.version}\nBuild: ${debugVersion.build}\nCommit: ${debugVersion.commit}\nDeploy Time: ${debugVersion.deployTime}\n${separator}\n`;
    const metadata = `Generation ID: ${generationId || 'N/A'}\nPrompt: ${selectedPrompt?.name || 'N/A'}\nÉtape: ${stepInfo.step}/${stepInfo.total}\nTemps écoulé: ${formatElapsedTime(elapsedTime)}\nÉvénements: ${debugLogs.length}\n${separator}\n`;
    const logsText = debugLogs.map(log => `[${log.timestamp}] ${log.message}`).join('\n');
    const fullText = header + separator + versionInfo + metadata + logsText;
    
    navigator.clipboard.writeText(fullText).then(() => {
      toast.success('📋 Logs copiés dans le presse-papier');
    }).catch((err) => {
      toast.error('❌ Erreur lors de la copie');
      console.error('Copy error:', err);
    });
  };

  const exportDiagnostic = () => {
    const stepInfo = getCurrentPipelineStep();
    const header = `🔍 NEXORA AI - Diagnostic Streaming\n`;
    const separator = '═'.repeat(60) + '\n';
    const date = new Date().toLocaleString('fr-FR');
    const versionInfo = `Debug Version: ${debugVersion.version}\nBuild: ${debugVersion.build}\nCommit: ${debugVersion.commit}\nDeploy Time: ${debugVersion.deployTime}\n${separator}\n`;
    const metadata = `Date: ${date}\nGeneration ID: ${generationId || 'N/A'}\nPrompt: ${selectedPrompt?.name || 'N/A'}\nÉtape: ${stepInfo.step}/${stepInfo.total}\nTemps écoulé: ${formatElapsedTime(elapsedTime)}\nÉvénements: ${debugLogs.length}\n${separator}\n`;
    const logsText = debugLogs.map(log => `[${log.timestamp}] ${log.message}`).join('\n');
    const fullText = header + separator + versionInfo + metadata + logsText;
    
    // Créer un blob et télécharger
    const blob = new Blob([fullText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nexora-diagnostic-${generationId || 'unknown'}-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('📤 Diagnostic exporté avec succès');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-slate-400">Chargement des prompts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Code className="inline mr-3 text-cyan-400" size={36} />
              Prompt Studio
            </h1>
            <p className="text-slate-400 text-lg">Éditeur professionnel avec bibliothèque de prompts</p>
          </div>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
            data-testid="create-prompt-btn"
          >
            <Plus size={20} className="inline mr-2" />
            Nouveau Prompt
          </motion.button>
        </div>
      </div>

      {/* Prompts Grid */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
        {prompts.map((prompt, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-200"
            data-testid={`prompt-${index}`}
          >
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Code className="text-cyan-400" size={20} />
                  <h3 className="text-xl font-semibold text-white">{prompt.name}</h3>
                </div>
                <span className="text-xs px-3 py-1 bg-indigo-500/20 text-indigo-400 rounded-full border border-indigo-500/30">
                  {prompt.category}
                </span>
              </div>
              <p className="text-sm text-slate-400 mb-4">{prompt.description}</p>
              
              {/* Template Preview */}
              <div className="bg-slate-900/50 rounded-xl p-4 mb-4">
                <pre className="text-xs text-slate-300 whitespace-pre-wrap font-mono">
                  {prompt.template.substring(0, 200)}{prompt.template.length > 200 ? '...' : ''}
                </pre>
              </div>

              {/* Variables */}
              {prompt.variables && prompt.variables.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {prompt.variables.map((v, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-md border border-cyan-500/30">
                      {v}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleExecutePrompt(prompt)}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all text-sm"
                data-testid={`execute-${index}`}
              >
                <Play size={16} className="inline mr-2" />
                Exécuter
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleEditPrompt(prompt)}
                className="px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/20 transition-all"
                data-testid={`edit-prompt-${index}`}
              >
                <Edit2 size={16} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => handleDeletePrompt(prompt.id, prompt.name)}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg border border-red-500/30 hover:bg-red-500/20 transition-all"
                data-testid={`delete-prompt-${index}`}
              >
                <Trash2 size={16} />
              </motion.button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={handleModalClose}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold text-white mb-6">
              {editingPrompt ? 'Modifier le Prompt' : 'Créer un Prompt'}
            </h2>
            <form onSubmit={handleCreatePrompt}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Nom</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                      required
                      data-testid="prompt-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-300 mb-2">Catégorie</label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                      data-testid="prompt-category-select"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    required
                    data-testid="prompt-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Template (utilisez {'{{variable}}'} pour les variables)
                  </label>
                  <textarea
                    value={formData.template}
                    onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500 h-40 font-mono text-sm"
                    required
                    placeholder="Ex: Créez une description de produit pour {{product_name}} de la marque {{brand}}..."
                    data-testid="prompt-template-input"
                  />
                  {formData.template && extractVariables(formData.template).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="text-xs text-slate-400">Variables détectées:</span>
                      {extractVariables(formData.template).map((v, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded-md">
                          {v}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="flex-1 px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
                  data-testid="submit-prompt-btn"
                >
                  {editingPrompt ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Execute Modal */}
      {showExecuteModal && selectedPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => !generating && setShowExecuteModal(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-slate-900 border border-white/10 rounded-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">{selectedPrompt.name}</h2>
              
              {/* Streaming Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-400">Streaming</span>
                <button
                  onClick={() => setIsStreaming(!isStreaming)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${isStreaming ? 'bg-cyan-500' : 'bg-slate-600'}`}
                  disabled={generating}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${isStreaming ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
            
            {/* Streaming Metadata */}
            {streamingMetadata && (
              <div className="mb-4 p-3 bg-slate-800/50 rounded-lg border border-cyan-500/20">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-400">Model: {streamingMetadata.model}</span>
                  <span className="text-slate-400">Temperature: {streamingMetadata.temperature?.toFixed(2)}</span>
                  {streamingMetadata.rate_limit && (
                    <span className="text-cyan-400">{streamingMetadata.rate_limit.remaining}/{streamingMetadata.rate_limit.max_calls} remaining</span>
                  )}
                </div>
              </div>
            )}
            
            {/* Variables Input */}
            <div className="space-y-4 mb-6">
              {selectedPrompt.variables.map((v, i) => (
                <div key={i}>
                  <label className="block text-sm font-medium text-slate-300 mb-2 capitalize">{v}</label>
                  <input
                    type="text"
                    value={variables[v] || ''}
                    onChange={(e) => setVariables({ ...variables, [v]: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-800 border border-white/10 rounded-xl text-white focus:outline-none focus:border-cyan-500"
                    placeholder={`Entrez ${v}...`}
                    data-testid={`variable-${v}`}
                    disabled={generating}
                  />
                </div>
              ))}
            </div>

            {/* Generate / Stop Buttons */}
            <div className="flex gap-3 mb-6">
              {!generating ? (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGenerate}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all"
                  data-testid="generate-btn"
                >
                  <Play size={20} className="inline mr-2" />
                  Générer {isStreaming && '(Streaming)'}
                </motion.button>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleStopGeneration}
                  className="flex-1 px-6 py-4 bg-red-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-red-500/30 transition-all"
                  data-testid="stop-btn"
                >
                  ■ Arrêter
                </motion.button>
              )}
            </div>

            {/* Generated Text with Streaming Animation */}
            {(generatedText || generating) && (
              <>
                <div className="bg-slate-800 border border-white/10 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Résultat
                    {generating && generationStatus && (
                      <span className="ml-2 text-sm text-cyan-400 animate-pulse">
                        {generationStatus}
                      </span>
                    )}
                    {!generating && generatedText && (
                      <span className="ml-2 text-sm text-green-400">
                        ✅ Génération terminée
                      </span>
                    )}
                  </h3>
                  <div className="text-slate-300 whitespace-pre-wrap font-mono text-sm leading-relaxed" data-testid="generated-text">
                    {generatedText}
                    {generating && isStreaming && (
                      <span className="inline-block w-2 h-4 bg-cyan-400 ml-1 animate-pulse" />
                    )}
                  </div>
                  {!generating && generatedText && (
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => navigator.clipboard.writeText(generatedText)}
                        className="px-4 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-sm"
                      >
                        <Copy size={16} className="inline mr-2" />
                        Copier
                      </button>
                      <div className="flex-1" />
                      <span className="text-xs text-slate-500">{generatedText.length} caractères</span>
                    </div>
                  )}
                </div>

                {/* Debug Panel - Mobile Diagnostic (P0 Streaming Bug) */}
                {debugLogs.length > 0 && (
                  <div className="mt-4 bg-slate-900 border border-amber-500/30 rounded-xl overflow-hidden">
                    {/* Debug Panel Header with Toggle */}
                    <button
                      onClick={() => setShowDebugPanel(!showDebugPanel)}
                      className="w-full px-4 py-3 flex items-center justify-between bg-amber-500/10 hover:bg-amber-500/20 transition-all"
                    >
                      <div className="flex flex-col items-start gap-1">
                        <div className="flex items-center gap-2">
                          <span className="text-amber-400 font-semibold text-sm">🔍 Panneau de Diagnostic</span>
                          <span className="text-xs text-slate-400">({debugLogs.length} événements)</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                          {generationId && (
                            <span className="text-slate-400 font-mono">
                              ID: {generationId}
                            </span>
                          )}
                          <span className="text-cyan-400 font-semibold">
                            Étape {getCurrentPipelineStep().step}/{getCurrentPipelineStep().total}
                          </span>
                          <span className="text-green-400 font-mono">
                            ⏱️ {formatElapsedTime(elapsedTime)}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs flex-wrap">
                          <span className="text-purple-400 font-mono">
                            Debug: {debugVersion.version}
                          </span>
                          <span className="text-slate-500 font-mono">
                            Build: {debugVersion.build}
                          </span>
                          <span className="text-slate-500 font-mono">
                            Commit: {debugVersion.commit}
                          </span>
                        </div>
                      </div>
                      <span className="text-amber-400 text-lg">
                        {showDebugPanel ? '▼' : '▶'}
                      </span>
                    </button>

                    {/* Debug Panel Content */}
                    {showDebugPanel && (
                      <div className="p-4 max-h-96 overflow-y-auto">
                        {/* Debug Logs List */}
                        <div className="space-y-1 font-mono text-xs">
                          {debugLogs.map((log, index) => {
                            let textColor = 'text-slate-300';
                            let icon = '•';
                            
                            if (log.type === 'error') {
                              textColor = 'text-red-400';
                              icon = '❌';
                            } else if (log.type === 'success') {
                              textColor = 'text-green-400';
                              icon = '✅';
                            } else if (log.type === 'warning') {
                              textColor = 'text-amber-400';
                              icon = '⚠️';
                            } else if (log.type === 'backend') {
                              textColor = 'text-blue-400';
                              icon = '🔧';
                            } else if (log.type === 'header') {
                              textColor = 'text-cyan-400 font-bold';
                              icon = '';
                            }

                            return (
                              <div key={index} className={`${textColor} flex gap-2`}>
                                <span className="text-slate-500 min-w-[60px]">{log.timestamp}</span>
                                <span className="min-w-[20px]">{icon}</span>
                                <span className="flex-1 break-words">{log.message}</span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Summary Stats & Export Buttons */}
                        <div className="mt-4 pt-4 border-t border-slate-700 space-y-3">
                          {/* Boutons Export */}
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              onClick={copyDebugLogs}
                              className="px-4 py-3 bg-cyan-500/10 text-cyan-400 rounded-lg border border-cyan-500/30 hover:bg-cyan-500/20 transition-all text-sm font-semibold flex items-center justify-center gap-2"
                            >
                              <Copy size={16} />
                              📋 Copier
                            </button>
                            <button
                              onClick={exportDiagnostic}
                              className="px-4 py-3 bg-green-500/10 text-green-400 rounded-lg border border-green-500/30 hover:bg-green-500/20 transition-all text-sm font-semibold flex items-center justify-center gap-2"
                            >
                              <Download size={16} />
                              📤 Exporter
                            </button>
                          </div>
                          
                          {/* Stats */}
                          {!generating && (
                            <div className="grid grid-cols-2 gap-3 text-xs">
                              <div className="bg-slate-800/50 rounded-lg p-3">
                                <div className="text-slate-400 mb-1">Total événements</div>
                                <div className="text-white font-semibold text-lg">{debugLogs.length}</div>
                              </div>
                              <div className="bg-slate-800/50 rounded-lg p-3">
                                <div className="text-slate-400 mb-1">Texte généré</div>
                                <div className="text-white font-semibold text-lg">{generatedText.length} car.</div>
                              </div>
                              <div className="bg-slate-800/50 rounded-lg p-3 col-span-2">
                                <div className="text-slate-400 mb-1">Deltas reçus</div>
                                <div className="text-white font-semibold text-lg">
                                  {debugLogs.filter(log => log.message.includes('Delta #')).length}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PromptStudio;